"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import GridRow from "../common/grid-row/grid-row";
import GridColumn from "../common/grid-column/grid-column";
import { Homepage, Page } from "@/sanity/sanity.types";
import imageUrl from "@/sanity/utilities/imageUrl";
import HomeTrust from "./home-trust/home-trust";
import HomeCTAStrip from "./home-cta-strip/home-cta-strip";
import HomeReviews from "./home-reviews/home-reviews";
import Card, { CardStyle } from "../common/card/card";
import HomePoints from "./home-points/home-points";
import { ArticleData } from "@/sanity/interfaces";
import ResourcesArticleBlockAuto from "../resources/resources-article-block/resources-article-block-auto";
import HeroComponent from "./home-hero";
import { usePerformance } from "@/contexts/performance-context";
import { ImageLoading } from "@/utilities/imageLoading";
import { useAs202Decision } from "@/hooks/use-as202-decision";
import FinancialHealthBanner, { AS202_BANNER_HREF_BY_VARIANT } from "./financial-health-banner";
interface IHomeProps {
  data: Homepage;
  beforeQueryTime: number;
  afterQueryTime: number;
  latestArticles?: ArticleData[];
}
const Home = props => {
  const $ = _c(43);
  const {
    data,
    beforeQueryTime,
    afterQueryTime,
    latestArticles
  } = props;
  const {
    ctaStrip,
    hero,
    quickOptions,
    homeAnimatedMobileBackground,
    homeAnimatedMobile1,
    homeAnimatedMobile2,
    proof,
    testimonials,
    tools,
    trustBar
  } = data;
  const {
    checkTimeElapsed
  } = usePerformance();
  const [heroHeight, setHeroHeight] = useState(631);
  let t0;
  if ($[0] !== afterQueryTime || $[1] !== beforeQueryTime || $[2] !== checkTimeElapsed) {
    t0 = () => {
      checkTimeElapsed(beforeQueryTime, "Home page before query started");
      checkTimeElapsed(afterQueryTime, "Home page after query completed");
    };
    $[0] = afterQueryTime;
    $[1] = beforeQueryTime;
    $[2] = checkTimeElapsed;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[5] !== hero) {
    t2 = () => {
      if (hero?.background?.asset) {
        const img = document.createElement("img");
        img.src = imageUrl(hero.background.asset).url();
        img.onload = () => {
          setHeroHeight(img.height);
        };
      }
    };
    t3 = [hero];
    $[5] = hero;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  const {
    shouldRunAs202,
    isDecisionReadyForAs202,
    variationKeyAs202,
    dma: dmaAs202,
    dmaGroup: dmaGroupAs202
  } = useAs202Decision();
  const shouldShowAS202 = isDecisionReadyForAs202 && shouldRunAs202;
  let t4;
  if ($[8] !== hero || $[9] !== heroHeight) {
    t4 = hero && <HeroComponent hero={hero} heroHeight={heroHeight} />;
    $[8] = hero;
    $[9] = heroHeight;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== trustBar) {
    t5 = trustBar && <HomeTrust data={trustBar} />;
    $[11] = trustBar;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== dmaAs202 || $[14] !== dmaGroupAs202 || $[15] !== quickOptions || $[16] !== shouldShowAS202 || $[17] !== variationKeyAs202) {
    t6 = quickOptions && <GridRow className="!gap-y-6"><GridColumn><h2 className="mb-4 text-center md:mb-0">{quickOptions.heading}</h2></GridColumn>{quickOptions.items?.map(_temp)}{shouldShowAS202 && <GridColumn><FinancialHealthBanner experimentId="AS202" dma={dmaAs202} dmaGroup={dmaGroupAs202} variationKey={variationKeyAs202} href={AS202_BANNER_HREF_BY_VARIANT[variationKeyAs202]} /></GridColumn>}</GridRow>;
    $[13] = dmaAs202;
    $[14] = dmaGroupAs202;
    $[15] = quickOptions;
    $[16] = shouldShowAS202;
    $[17] = variationKeyAs202;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== homeAnimatedMobile1 || $[20] !== homeAnimatedMobile2 || $[21] !== homeAnimatedMobileBackground || $[22] !== proof) {
    t7 = proof && <HomePoints data={proof} homeAnimatedMobileBackground={homeAnimatedMobileBackground} homeAnimatedMobile1={homeAnimatedMobile1} homeAnimatedMobile2={homeAnimatedMobile2} />;
    $[19] = homeAnimatedMobile1;
    $[20] = homeAnimatedMobile2;
    $[21] = homeAnimatedMobileBackground;
    $[22] = proof;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== tools) {
    t8 = !!tools?.tools?.length && <><GridRow className="lg:!mt-[20px]">{tools.heading && <GridColumn><h2 className="text-center">{tools.heading}</h2></GridColumn>}{tools.tools?.map(_temp2)}</GridRow></>;
    $[24] = tools;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== testimonials) {
    t9 = testimonials && <HomeReviews data={testimonials} />;
    $[26] = testimonials;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== latestArticles) {
    t10 = latestArticles && <ResourcesArticleBlockAuto articles={latestArticles} title="Recent Articles" imageLoading={ImageLoading.LAZY} />;
    $[28] = latestArticles;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
    t11 = <div className="flex flex-col gap-12 pb-12 pt-12 md:gap-24 md:pt-24">{t6}{t7}{t8}{t9}{t10}</div>;
    $[30] = t10;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== ctaStrip) {
    t12 = ctaStrip && <HomeCTAStrip data={ctaStrip} />;
    $[36] = ctaStrip;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t11 || $[39] !== t12 || $[40] !== t4 || $[41] !== t5) {
    t13 = <div className="mx-auto">{t4}{t5}{t11}{t12}</div>;
    $[38] = t11;
    $[39] = t12;
    $[40] = t4;
    $[41] = t5;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
};
export default Home;
function _temp(item, index) {
  const {
    cta,
    icon,
    summary,
    title
  } = item;
  if (!cta?.link || !icon || !summary || !title) {
    return null;
  }
  return <GridColumn key={index} lg={4}><Card description={summary} imgUrl={icon?.asset ? imageUrl(icon?.asset).width(58).url() : undefined} linkLabel={cta.link.label} linkUrl={cta.link.href ?? "/"} title={title} hoverEffect={true} imgWidth={58} imgHeight={58} ctaId={cta?.link?.ctaId ?? ""} ctaValue={cta?.link?.ctaValue ?? ""} relValue={cta?.link?.nofollow} /></GridColumn>;
}
function _temp2(tool, index_0) {
  const {
    cta: cta_0,
    image,
    summary: summary_0,
    title: title_0
  } = tool;
  if (!cta_0?.link || !image || !summary_0 || !title_0) {
    return null;
  }
  return <GridColumn key={index_0} lg={4}><Card className="!bg-white" description={summary_0} imgUrl={image?.asset ? imageUrl(image.asset).width(327).url() : undefined} linkLabel={cta_0.link.label} linkUrl={cta_0.link.href ?? "/"} title={title_0} imgWidth={327} imgHeight={179} style={CardStyle.Tertiary} ctaId={cta_0?.link?.ctaId ?? ""} ctaValue={cta_0?.link?.ctaValue ?? ""} relValue={cta_0?.link?.nofollow} /></GridColumn>;
}